import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import FieldSelector, {
  LabelType,
} from 'modules/reports/components/Editor/Carcasa/FormFields/FieldSelector'
import Aggregations from 'modules/reports/components/Editor/Carcasa/FormFields/GroupByField/Aggregations'
import Options from 'modules/reports/components/Editor/Carcasa/FormFields/GroupByField/Options'
import { FieldType } from 'modules/reports/components/Editor/Field/types'
import { FieldSelectionType, useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { TYPES, translateAggregationFunction } from 'modules/reports/lib/Constants'
import type { ReportEditorStore } from 'modules/reports/lib/Store'
import { getDefaultAggregationFunction } from 'modules/reports/lib/fieldHelper'
import fieldAttributes from 'modules/reports/store/collections/fields'
import type Field from 'modules/reports/store/models/Field'
import type { AbilityType } from 'modules/reports/store/models/types'

const EMPTY_ABILITY: AbilityType[] = []
const EMPTY_FIELD: FieldType[] = []

const ON_REMOVE_FIELD = () => {}

interface Props {
  store: ReportEditorStore
}

const createNewStore = createComputed((newStore: ReportEditorStore) => {
  return newStore
})

const GroupByField = ({ store }: Props) => {
  const { trackFieldChange } = useReportTracking()
  const newStore = createNewStore(store)

  const [groupingFields, aggregationFields] = _.partition(
    newStore.get().projection.selectedFieldTypesByUiGroup(),
    (f) => {
      return _.isEmpty(f.form.get('function').value)
    }
  )

  const handleClear = useUnsafeCallback(() => {
    newStore
      .get()
      .projection.selectedFieldTypesByUiGroup()
      .forEach((f) => {
        const currentFunction = f.form.get('function')
        currentFunction.set(null)
      })
  }, [newStore])

  // On select fields, set a default aggregation method to all the others
  const handleSelectFields = useUnsafeCallback(
    (fields: Field[]) => {
      const newAggregationFields = newStore
        .get()
        .projection.selectedFieldTypesByUiGroup()
        .filter((fieldType) => {
          const currentGroupByPaths = fields.map((field) => field.get('path'))

          return !currentGroupByPaths.includes(fieldType.field.get('path'))
        })

      newAggregationFields.forEach((newAggregationField) => {
        const currentFunction = newAggregationField.form.get('function')

        if (!currentFunction.value) {
          const defaultFunction = getDefaultAggregationFunction(newAggregationField.field)

          currentFunction.set(defaultFunction)
        }
      })

      trackFieldChange(FieldSelectionType.GroupBy)
    },
    [trackFieldChange, newStore]
  )

  const availableFields = useUnsafeCallback((): string[] => {
    const fields = fieldAttributes.models
    const currentFields: string[] = newStore
      .get()
      .projection.selectedFieldTypesByUiGroup()
      .map((fieldType) => fieldType.field.id)
    const usedFields: string[] = fields
      .map((e: Field) => e.get('path'))
      .filter((path): path is string => Boolean(path))

    return usedFields.filter((path) => {
      return !currentFields.includes(path)
    })
  }, [newStore])

  const label = useMemo(
    () =>
      ({
        text: i18n.t('custom_reports.fields.table.group_by.title'),
        tooltipText: i18n.tView('custom_reports.fields.table.group_by.tooltip', {}),
        optional: true,
      }) satisfies LabelType,
    []
  )

  const showSelector = aggregationFields.length === 0
  const disableGroupBy = newStore.get().projection.selectedFieldTypesByUiGroup().length <= 1

  return (
    <>
      {showSelector && (
        <FieldSelector
          disabled={disableGroupBy}
          variables={EMPTY_FIELD}
          fieldTypes={EMPTY_FIELD}
          excludePaths={availableFields()}
          fieldAbilityTypes={EMPTY_ABILITY}
          chartType={store.reportStruct.type}
          label={label}
          onSelectFields={handleSelectFields}
          onRemoveField={ON_REMOVE_FIELD}
          isFilter
          selectMultiple
          excludeIdentifiers
          excludeTypes={[TYPES.cohort]}
          buttonLabel={i18n.t('custom_reports.fields.table.group_by.button')}
          modalTitle={i18n.t('custom_reports.fields.group_by')}
          modalDescription={i18n.t('custom_reports.fields.group_by_description')}
        />
      )}
      {!showSelector && (
        <Aggregations
          currentGroupBy={groupingFields.map((g) => g.field)}
          label={label}
          clear={handleClear}
        >
          {aggregationFields.map((aggregationField, index) => (
            <Box key={index} flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
              <Box flexDirection="row" paddingLeft="s32">
                <Text.Normal size="100" weight="medium" color="grey1000">
                  {aggregationField.field.get('label')} : &nbsp;
                </Text.Normal>
                <Text.Normal size="100" color="grey1000">
                  {translateAggregationFunction(aggregationField.form.get('function').value)}
                </Text.Normal>
              </Box>
              <Options field={aggregationField} />
            </Box>
          ))}
        </Aggregations>
      )}
    </>
  )
}

export default observer(GroupByField)
